import { FunctionComponent, useCallback, useState, useMemo } from 'react'
import { PatientsListExtendedActionsProps } from './PatientsListExtendedActions.model'
import { DatePicker, SearchInput, TooltipWrapper, Icon, MultiSelectSearch } from '../../../shared'
import { ColorsV2 } from '../../../../design-system/colors_v2'
import { AtcClassSelect, ActivePrinciplesSelect } from '../../../drug'
import { formatISO } from 'date-fns'
import { useDebouncedFilters } from '../../../../hooks/utils'
import { AtcClass } from '../../../../model/Drug'
import styles from './PatientsListExtendedActions.module.scss'
import { Sex } from '../../../../model/Patient'
import { MultiSelectOption } from '../../../../model/SelectOption'
import { isDefined } from '@follow/cdk'

const patientSexOptions: Array<MultiSelectOption<Sex>> = [
  { value: Sex.MALE, label: 'Masculin', icon: 'male' },
  { value: Sex.FEMALE, label: 'Féminin', icon: 'female' },
  { value: Sex.UNKNOWN, label: 'Indéterminé' },
]

export const PatientsListExtendedActions: FunctionComponent<PatientsListExtendedActionsProps> = ({
  onSearchFiltersChange,
  filters: {
    drugSearch,
    prescriptionReason,
    drugStartDate,
    drugEndDate,
    atcCodeExternalId,
    activePrinciple,
    sex,
  },
}) => {
  const [debouncedFilters, setDebouncedFilters] = useState({
    drugSearch: drugSearch,
    prescriptionReason: prescriptionReason,
  })

  useDebouncedFilters(debouncedFilters, onSearchFiltersChange)

  const handleSelectAtcClass = useCallback(
    (atcClass: AtcClass | null) => {
      onSearchFiltersChange({ atcCodeExternalId: atcClass ? atcClass.id : null })
    },
    [onSearchFiltersChange],
  )

  const sexValue = useMemo(
    () =>
      sex
        .map((sexItem) => patientSexOptions.find(({ value }) => value === sexItem))
        .filter(isDefined),
    [sex],
  )

  return (
    <div className={styles.patientsListExtendedActionsContainer}>
      <MultiSelectSearch
        placeholder="Rechercher par sexe"
        icon="sex"
        testId="multi_select_search-patient-by_sex-search"
        value={sexValue}
        options={patientSexOptions}
        onSelect={(selected) => onSearchFiltersChange({ sex: selected.map(({ value }) => value) })}
      />
      <div className={styles.prescriptionReasonFilter}>
        <SearchInput
          value={debouncedFilters.prescriptionReason}
          placeholder="Rechercher par motif de prescription"
          onChange={({ target: { value } }) =>
            setDebouncedFilters((prev) => ({ ...prev, prescriptionReason: value }))
          }
          testId="input-prescription-by_reason-search"
        />
      </div>
      <div className={styles.activePrincipleFilter}>
        <ActivePrinciplesSelect
          setActivePrincipleSelected={(value) => {
            onSearchFiltersChange({
              activePrinciple: value ? value.name : '',
            })
          }}
          colorPreset="light"
          placeHolder="Rechercher par principe actif"
          initialSearch={activePrinciple}
        />
      </div>
      <div className={styles.atcCodeFilter}>
        <AtcClassSelect
          classId={atcCodeExternalId ? atcCodeExternalId : undefined}
          colorPreset="light"
          onClassSelected={handleSelectAtcClass}
        />
      </div>
      <div className={styles.prescribedDrugFilter}>
        <SearchInput
          icon="pills"
          value={debouncedFilters.drugSearch}
          placeholder="Rechercher par médicament prescrit"
          onChange={({ target: { value } }) =>
            setDebouncedFilters((prev) => ({ ...prev, drugSearch: value }))
          }
          testId="input-prescription-by_drug-search"
        />
      </div>
      <div className={styles.treatmentStartDateFilter}>
        <DatePicker
          label="Début"
          icon="calendar"
          colorPreset="light"
          placeholder="Début"
          value={drugStartDate}
          onChange={(value) => onSearchFiltersChange({ drugStartDate: value && formatISO(value) })}
          inputType="search"
          showCalendarButton={false}
          testId="date_picker-patient_list-drug_starting_date-filter"
        />
      </div>
      <div className={styles.treatmentEndDateFilter}>
        <DatePicker
          label="Fin"
          icon="calendar"
          colorPreset="light"
          placeholder="Fin"
          value={drugEndDate}
          onChange={(value) => onSearchFiltersChange({ drugEndDate: value && formatISO(value) })}
          inputType="search"
          showCalendarButton={false}
          testId="date_picker-patient_list-drug_ending_date-filter"
        />
      </div>
      <TooltipWrapper
        content="La recherche trouvera les patients qui ont été sous traitement de ce médicament au moins un jour pendant la période renseignée"
        size="small"
        pointerDirection="right"
      >
        <Icon size="micro" icon="infoCircle" color={ColorsV2.backgroundPrimary} />
      </TooltipWrapper>
    </div>
  )
}
